import { useCallback, useEffect, useState } from "react";
import { AlertCircle, Check, CheckSquare, Image as ImageIcon, Loader2, Minus, Pencil, Plus, Square, Trash2, Upload, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api, ApiError, type CatalogCourse, type CatalogStream, type CatalogUniversity } from "../../lib/api";
import { canAccess } from "../../lib/auth-tokens";
import { ConfirmDialog, ModalShell } from "../../components/admin/AdminModals";
import { AdminEmptyState, AdminFilterBar, AdminPageHeader } from "../../components/admin/AdminUI";
import { invalidateStudySubjectsCache } from "../../lib/useStudySubjects";
import { COURSE_TYPES } from "../../lib/extractorPrompt";

function useAdminToken() {
  const { tokens } = useAuth();
  return tokens?.accessToken ?? null;
}

function parseUniversitiesList(text: string): { name: string; short_name: string; region?: string }[] {
  const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
  const items: { name: string; short_name: string; region?: string }[] = [];
  for (const line of lines) {
    const parts = line.split(",").map(p => p.trim());
    if (parts.length >= 2) {
      items.push({ name: parts[0], short_name: parts[1].toUpperCase(), region: parts[2] || undefined });
    } else if (parts.length === 1 && parts[0]) {
      const words = parts[0].split(/\s+/);
      const code = words.length > 1 ? words.map(w => w[0]).join("").toUpperCase() : parts[0].slice(0, 4).toUpperCase();
      items.push({ name: parts[0], short_name: code });
    }
  }
  return items;
}

function parseCoursesList(
  text: string,
  defaultStreamId?: string,
  defaultSemester?: number | null,
): { name: string; code: string; description?: string; stream_id?: string; semester?: number | null }[] {
  const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
  const items: { name: string; code: string; description?: string; stream_id?: string; semester?: number | null }[] = [];
  for (const line of lines) {
    const parts = line.split(",").map(p => p.trim());
    if (parts.length >= 2) {
      items.push({
        name: parts[0],
        code: parts[1].toUpperCase(),
        description: parts[2] || undefined,
        stream_id: defaultStreamId || undefined,
        semester: defaultSemester !== undefined ? defaultSemester : 1,
      });
    } else if (parts.length === 1 && parts[0]) {
      const words = parts[0].split(/\s+/);
      const code = words.length > 1 ? (words.map(w => w.slice(0, 3)).join("").toUpperCase()).slice(0, 8) : parts[0].slice(0, 6).toUpperCase();
      items.push({
        name: parts[0],
        code,
        stream_id: defaultStreamId || undefined,
        semester: defaultSemester !== undefined ? defaultSemester : 1,
      });
    }
  }
  return items;
}

function parseStreamsList(text: string): { name: string; code?: string; description?: string }[] {
  const lines = text.split(/[\n,]+/).map(l => l.trim()).filter(Boolean);
  const items: { name: string; code?: string; description?: string }[] = [];
  for (const line of lines) {
    const parts = line.split(":").map(p => p.trim());
    const name = parts[0];
    const code = (parts[1] || name).trim().toLowerCase().replace(/\s+/g, "_");
    items.push({ name, code });
  }
  return items;
}

export function AdminCatalog() {
  const { tokens } = useAuth();
  const token = useAdminToken();
  const canWrite = canAccess(tokens, "catalog.write");
  const canView = canWrite || canAccess(tokens, "settings.read") || canAccess(tokens, "users.write");

  const [unis, setUnis] = useState<CatalogUniversity[]>([]);
  const [courses, setCourses] = useState<CatalogCourse[]>([]);
  const [streams, setStreams] = useState<CatalogStream[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState<"universities" | "courses" | "streams">("universities");
  const [search, setSearch] = useState("");
  const [streamFilter, setStreamFilter] = useState<string>("all");
  const [semesterFilter, setSemesterFilter] = useState<string>("all");

  const [selectedUnis, setSelectedUnis] = useState<Set<string>>(new Set());
  const [selectedCourses, setSelectedCourses] = useState<Set<string>>(new Set());
  const [bulkDeleteTarget, setBulkDeleteTarget] = useState<"unis" | "courses" | null>(null);
  const [bulkMoveTarget, setBulkMoveTarget] = useState<{ targetSemester: number | null; count: number } | null>(null);
  const [impactDialog, setImpactDialog] = useState<{
    courseId: string;
    courseName: string;
    currentSemester: number | null;
    newSemester: number | null;
    resourceCount: number;
    affectedStudentsCount: number;
    onConfirm: () => Promise<void>;
  } | null>(null);

  const [premiumConfirm, setPremiumConfirm] = useState<{
    course: CatalogCourse;
    targetState: boolean;
    resourceCount: number;
  } | null>(null);

  const [editUni, setEditUni] = useState<CatalogUniversity | null>(null);
  const [editCourse, setEditCourse] = useState<CatalogCourse | null>(null);
  const [editStream, setEditStream] = useState<CatalogStream | null>(null);
  const [creatingUni, setCreatingUni] = useState(false);
  const [creatingCourse, setCreatingCourse] = useState(false);
  const [creatingStream, setCreatingStream] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: "uni" | "course" | "stream"; id: string; label: string } | null>(null);
  const [busy, setBusy] = useState(false);

  // Single entry form state
  const [uniForm, setUniForm] = useState({ name: "", short_name: "", region: "", is_active: true });
  const [courseForm, setCourseForm] = useState<{
    name: string;
    code: string;
    description: string;
    stream_id: string;
    semester: number | null;
    image_url: string;
    course_type: string;
    extraction_profile: string;
  }>({
    name: "",
    code: "",
    description: "",
    stream_id: "",
    semester: 1,
    image_url: "",
    course_type: "LANGUAGE",
    extraction_profile: "",
  });
  const [uploadingCourseImage, setUploadingCourseImage] = useState(false);
  const [streamForm, setStreamForm] = useState({ name: "", code: "", description: "", is_active: true });
  const [formError, setFormError] = useState("");

  // List/bulk registration state
  const [uniCreateMode, setUniCreateMode] = useState<"single" | "list">("single");
  const [courseCreateMode, setCourseCreateMode] = useState<"single" | "list">("single");
  const [streamCreateMode, setStreamCreateMode] = useState<"single" | "list">("single");

  const [uniBatchText, setUniBatchText] = useState("");
  const [courseBatchText, setCourseBatchText] = useState("");
  const [courseBatchStreamId, setCourseBatchStreamId] = useState("");
  const [courseBatchSemester, setCourseBatchSemester] = useState<number | null>(1);
  const [streamBatchText, setStreamBatchText] = useState("");

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    Promise.all([
      api.admin.catalogUniversities(token),
      api.admin.catalogCourses(token),
      api.admin.catalogStreams(token),
    ])
      .then(([u, c, s]) => {
        setUnis(u.items);
        setCourses(c.items);
        setStreams(s.items);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : "Failed to load catalog"))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  function openCreateUni() {
    setUniForm({ name: "", short_name: "", region: "", is_active: true });
    setUniBatchText("");
    setUniCreateMode("single");
    setFormError("");
    setCreatingUni(true);
  }

  function openEditUni(u: CatalogUniversity) {
    setUniForm({
      name: u.name,
      short_name: u.short_name,
      region: u.region || "",
      is_active: u.isActive,
    });
    setFormError("");
    setEditUni(u);
  }

  function openCreateCourse() {
    setCourseForm({
      name: "",
      code: "",
      description: "",
      stream_id: "",
      semester: 1,
      image_url: "",
      course_type: "LANGUAGE",
      extraction_profile: "",
    });
    setCourseBatchText("");
    setCourseBatchStreamId("");
    setCourseBatchSemester(1);
    setCourseCreateMode("single");
    setFormError("");
    setCreatingCourse(true);
  }

  function openEditCourse(c: CatalogCourse) {
    setCourseForm({
      name: c.name,
      code: c.code,
      description: c.description || "",
      stream_id: c.streamId || "",
      semester: c.semester ?? null,
      image_url: c.imageUrl || "",
      course_type: c.courseType || c.course_type || "LANGUAGE",
      extraction_profile: c.extractionProfile || c.extraction_profile || "",
    });
    setFormError("");
    setEditCourse(c);
  }

  async function handleCourseImageUpload(file: File) {
    if (!token) return;
    if (!file.type.match(/^image\/(jpeg|png|webp)$/)) {
      setFormError("Only JPEG, PNG, and WebP images are allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setFormError("Image must be smaller than 5MB.");
      return;
    }
    setUploadingCourseImage(true);
    setFormError("");
    try {
      const res = await api.admin.uploadCourseImage(token, file, editCourse?.id);
      setCourseForm((prev) => ({ ...prev, image_url: res.imageUrl }));
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Failed to upload image");
    } finally {
      setUploadingCourseImage(false);
    }
  }

  function openCreateStream() {
    setStreamForm({ name: "", code: "", description: "", is_active: true });
    setStreamBatchText("");
    setStreamCreateMode("single");
    setFormError("");
    setCreatingStream(true);
  }

  function openEditStream(s: CatalogStream) {
    setStreamForm({
      name: s.name,
      code: s.code,
      description: s.description || "",
      is_active: s.isActive,
    });
    setFormError("");
    setEditStream(s);
  }

  async function saveUniversity(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    setBusy(true);
    setFormError("");
    try {
      if (editUni) {
        await api.admin.updateUniversity(token, editUni.id, {
          name: uniForm.name.trim(),
          short_name: uniForm.short_name.trim(),
          region: uniForm.region.trim() || null,
          is_active: uniForm.is_active,
        });
        setEditUni(null);
      } else if (uniCreateMode === "list") {
        const items = parseUniversitiesList(uniBatchText);
        if (items.length === 0) {
          setFormError("No universities parsed. Enter one per line: Name, ShortCode, Region");
          setBusy(false);
          return;
        }
        await api.admin.createUniversitiesBatch(token, items);
        setCreatingUni(false);
        setUniBatchText("");
      } else {
        await api.admin.createUniversity(token, {
          name: uniForm.name.trim(),
          short_name: uniForm.short_name.trim(),
          region: uniForm.region.trim() || undefined,
        });
        setCreatingUni(false);
      }
      load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleToggleCatalogCoursePremium(course: CatalogCourse) {
    if (!token) return;
    const currentState = Boolean(course.isPremium ?? course.is_premium ?? false);
    const targetState = !currentState;
    try {
      const coursesApi = api.admin.catalog?.courses || api.admin.courses;
      const impact = await coursesApi.getSemesterImpact(token, course.id);
      setPremiumConfirm({
        course,
        targetState,
        resourceCount: impact.resourceCount ?? course.resourceCount ?? 0,
      });
    } catch {
      setPremiumConfirm({
        course,
        targetState,
        resourceCount: course.resourceCount ?? 0,
      });
    }
  }

  async function confirmCatalogCoursePremium() {
    if (!token || !premiumConfirm) return;
    setBusy(true);
    try {
      const coursesApi = api.admin.catalog?.courses || api.admin.courses;
      await coursesApi.update(token, premiumConfirm.course.id, {
        is_premium: premiumConfirm.targetState,
      });
      setCourses((prev) =>
        prev.map((c) =>
          c.id === premiumConfirm.course.id
            ? { ...c, isPremium: premiumConfirm.targetState, is_premium: premiumConfirm.targetState }
            : c
        )
      );
      setPremiumConfirm(null);
    } catch (err: any) {
      setError(err instanceof ApiError ? err.message : (err?.message || "Failed to update course premium status"));
      setPremiumConfirm(null);
    } finally {
      setBusy(false);
    }
  }

  async function saveCourse(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    setBusy(true);
    setFormError("");
    try {
      if (editCourse) {
        if (editCourse.semester !== courseForm.semester) {
          const impact = await api.admin.getCourseSemesterImpact(token, editCourse.id, courseForm.semester);
          if (impact.hasImpact) {
            setBusy(false);
            const targetCourseId = editCourse.id;
            const targetCourseName = editCourse.name;
            const updatedData = {
              name: courseForm.name.trim(),
              code: courseForm.code.trim(),
              description: courseForm.description.trim() || null,
              stream_id: courseForm.stream_id || null,
              semester: courseForm.semester,
              image_url: courseForm.image_url.trim() || null,
            };
            setEditCourse(null);
            setImpactDialog({
              courseId: targetCourseId,
              courseName: targetCourseName,
              currentSemester: impact.currentSemester,
              newSemester: impact.newSemester,
              resourceCount: impact.resourceCount,
              affectedStudentsCount: impact.affectedStudentsCount,
              onConfirm: async () => {
                await api.admin.updateCourse(token, targetCourseId, updatedData);
                invalidateStudySubjectsCache();
                load();
              },
            });
            return;
          }
        }
        await api.admin.updateCourse(token, editCourse.id, {
          name: courseForm.name.trim(),
          code: courseForm.code.trim(),
          description: courseForm.description.trim() || null,
          stream_id: courseForm.stream_id || null,
          semester: courseForm.semester,
          image_url: courseForm.image_url.trim() || null,
          course_type: courseForm.course_type,
          extraction_profile: courseForm.extraction_profile?.trim() || null,
        });
        invalidateStudySubjectsCache();
        setEditCourse(null);
      } else if (courseCreateMode === "list") {
        const items = parseCoursesList(courseBatchText, courseBatchStreamId || undefined, courseBatchSemester);
        if (items.length === 0) {
          setFormError("No courses parsed. Enter one per line: Name, Code");
          setBusy(false);
          return;
        }
        await api.admin.createCoursesBatch(token, items);
        invalidateStudySubjectsCache();
        setCreatingCourse(false);
        setCourseBatchText("");
      } else {
        await api.admin.createCourse(token, {
          name: courseForm.name.trim(),
          code: courseForm.code.trim(),
          description: courseForm.description.trim() || undefined,
          stream_id: courseForm.stream_id || undefined,
          semester: courseForm.semester,
          image_url: courseForm.image_url.trim() || undefined,
          course_type: courseForm.course_type,
          extraction_profile: courseForm.extraction_profile?.trim() || undefined,
        });
        invalidateStudySubjectsCache();
        setCreatingCourse(false);
      }
      load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleInlineSemesterChange(course: CatalogCourse, newSem: number | null) {
    if (!token || course.semester === newSem) return;
    try {
      const impact = await api.admin.getCourseSemesterImpact(token, course.id, newSem);
      if (impact.hasImpact) {
        setImpactDialog({
          courseId: course.id,
          courseName: course.name,
          currentSemester: impact.currentSemester,
          newSemester: impact.newSemester,
          resourceCount: impact.resourceCount,
          affectedStudentsCount: impact.affectedStudentsCount,
          onConfirm: async () => {
            await api.admin.updateCourse(token, course.id, { semester: newSem });
            invalidateStudySubjectsCache();
            load();
          },
        });
      } else {
        await api.admin.updateCourse(token, course.id, { semester: newSem });
        invalidateStudySubjectsCache();
        load();
      }
    } catch (err: any) {
      setError(err instanceof ApiError ? err.message : (err?.message || "Failed to change course semester"));
    }
  }

  async function confirmBulkMove() {
    if (!token || !bulkMoveTarget) return;
    setBusy(true);
    setError("");
    try {
      await api.admin.bulkCoursesAction(token, {
        course_ids: Array.from(selectedCourses),
        action: "set_semester",
        semester: bulkMoveTarget.targetSemester,
      });
      invalidateStudySubjectsCache();
      setSelectedCourses(new Set());
      setBulkMoveTarget(null);
      load();
    } catch (err: any) {
      setError(err instanceof ApiError ? err.message : (err?.message || "Bulk move failed"));
      setBulkMoveTarget(null);
    } finally {
      setBusy(false);
    }
  }

  async function saveStream(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    setBusy(true);
    setFormError("");
    try {
      if (editStream) {
        await api.admin.updateStream(token, editStream.id, {
          name: streamForm.name.trim(),
          code: streamForm.code.trim().toLowerCase(),
          description: streamForm.description.trim() || null,
          is_active: streamForm.is_active,
        });
        setEditStream(null);
      } else if (streamCreateMode === "list") {
        const items = parseStreamsList(streamBatchText);
        if (items.length === 0) {
          setFormError("No streams parsed. Enter stream names separated by lines or commas.");
          setBusy(false);
          return;
        }
        await api.admin.createStreamsBatch(token, items);
        setCreatingStream(false);
        setStreamBatchText("");
      } else {
        await api.admin.createStream(token, {
          name: streamForm.name.trim(),
          code: streamForm.code.trim().toLowerCase() || undefined,
          description: streamForm.description.trim() || undefined,
          is_active: streamForm.is_active,
        });
        setCreatingStream(false);
      }
      load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!token || !deleteTarget) return;
    setBusy(true);
    try {
      if (deleteTarget.type === "uni") await api.admin.deleteUniversity(token, deleteTarget.id);
      else if (deleteTarget.type === "course") await api.admin.deleteCourse(token, deleteTarget.id);
      else if (deleteTarget.type === "stream") await api.admin.deleteStream(token, deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Delete failed");
      setDeleteTarget(null);
    } finally {
      setBusy(false);
    }
  }

  async function runBulkUniAction(action: "activate" | "deactivate") {
    if (!token || selectedUnis.size === 0) return;
    setBusy(true);
    setError("");
    try {
      await api.admin.bulkUniversitiesAction(token, {
        university_ids: Array.from(selectedUnis),
        action,
      });
      setSelectedUnis(new Set());
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Bulk action failed");
    } finally {
      setBusy(false);
    }
  }

  async function confirmBulkDelete() {
    if (!token || !bulkDeleteTarget) return;
    setBusy(true);
    setError("");
    try {
      if (bulkDeleteTarget === "unis") {
        await api.admin.bulkUniversitiesAction(token, {
          university_ids: Array.from(selectedUnis),
          action: "delete",
        });
        setSelectedUnis(new Set());
      } else {
        await api.admin.bulkCoursesAction(token, {
          course_ids: Array.from(selectedCourses),
          action: "delete",
        });
        setSelectedCourses(new Set());
      }
      setBulkDeleteTarget(null);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Bulk delete failed");
      setBulkDeleteTarget(null);
    } finally {
      setBusy(false);
    }
  }

  const filteredUnis = unis.filter((u) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (u.name && u.name.toLowerCase().includes(q)) ||
      (u.short_name && u.short_name.toLowerCase().includes(q)) ||
      (u.region && u.region.toLowerCase().includes(q))
    );
  });

  const unassignedCoursesCount = courses.filter((c) => c.semester == null).length;

  const filteredCourses = courses.filter((c) => {
    if (semesterFilter !== "all") {
      if (semesterFilter === "unassigned") {
        if (c.semester != null) return false;
      } else {
        if (c.semester !== Number(semesterFilter)) return false;
      }
    }
    if (streamFilter !== "all") {
      if (streamFilter === "unassigned") {
        if (c.streamId) return false;
      } else {
        if (c.streamId !== streamFilter) return false;
      }
    }
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (c.name && c.name.toLowerCase().includes(q)) ||
      (c.code && c.code.toLowerCase().includes(q)) ||
      (c.description && c.description.toLowerCase().includes(q))
    );
  });

  const filteredStreams = streams.filter((s) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (s.name && s.name.toLowerCase().includes(q)) ||
      (s.code && s.code.toLowerCase().includes(q)) ||
      (s.description && s.description.toLowerCase().includes(q))
    );
  });

  const allUnisSelected = filteredUnis.length > 0 && filteredUnis.every((u) => selectedUnis.has(u.id));
  const someUnisSelected = filteredUnis.some((u) => selectedUnis.has(u.id));

  function toggleAllUnis() {
    if (allUnisSelected) {
      setSelectedUnis(new Set());
    } else {
      setSelectedUnis(new Set(filteredUnis.map((u) => u.id)));
    }
  }

  function toggleUni(id: string) {
    setSelectedUnis((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const allCoursesSelected = filteredCourses.length > 0 && filteredCourses.every((c) => selectedCourses.has(c.id));
  const someCoursesSelected = filteredCourses.some((c) => selectedCourses.has(c.id));

  function toggleAllCourses() {
    if (allCoursesSelected) {
      setSelectedCourses(new Set());
    } else {
      setSelectedCourses(new Set(filteredCourses.map((c) => c.id)));
    }
  }

  function toggleCourse(id: string) {
    setSelectedCourses((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (!canView) {
    return (
      <div className="p-8">
        <p className="text-sm text-warning bg-warning/10 border border-warning/30 rounded-lg px-4 py-3">
          You do not have permission to view catalog entities.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20 text-muted-foreground">
        <Loader2 className="animate-spin mr-2" /> Loading catalog…
      </div>
    );
  }

  const uniModalOpen = creatingUni || !!editUni;
  const courseModalOpen = creatingCourse || !!editCourse;
  const streamModalOpen = creatingStream || !!editStream;

  return (
    <div className="p-8 space-y-6">
      <AdminPageHeader
        title="Universities, courses & streams"
        subtitle="Manage academic catalog, streams, and see linked resources"
        actions={
          canWrite ? (
            <button
              type="button"
              onClick={() => {
                if (tab === "universities") openCreateUni();
                else if (tab === "courses") openCreateCourse();
                else openCreateStream();
              }}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-on-accent text-sm font-semibold rounded-lg"
            >
              <Plus size={15} /> Add {tab === "universities" ? "university" : tab === "courses" ? "course" : "stream"}
            </button>
          ) : undefined
        }
      />
      {error && <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-4 py-3">{error}</p>}

      <div className="flex gap-2">
        {(["universities", "courses", "streams"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => {
              setTab(t);
              setSearch("");
            }}
            className={`px-4 py-2 rounded-lg text-sm font-semibold capitalize ${
              tab === t ? "bg-primary text-on-accent" : "bg-muted text-muted-foreground"
            }`}
          >
            {t} ({t === "universities" ? unis.length : t === "courses" ? courses.length : streams.length})
          </button>
        ))}
      </div>

      <AdminFilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={
          tab === "universities"
            ? "Search universities by name, short code, region..."
            : tab === "courses"
            ? "Search courses by code, name, description..."
            : "Search streams by name, code, description..."
        }
        selects={
          tab === "courses"
            ? [
                {
                  id: "semester",
                  label: "Semester",
                  value: semesterFilter,
                  options: [
                    { value: "all", label: "All semesters" },
                    { value: "1", label: "Semester 1" },
                    { value: "2", label: "Semester 2" },
                    { value: "unassigned", label: "Unassigned" },
                  ],
                },
                {
                  id: "stream",
                  label: "Stream",
                  value: streamFilter,
                  options: [
                    { value: "all", label: "All streams" },
                    ...streams.map((s) => ({ value: s.id, label: `${s.name} (${s.code})` })),
                    { value: "unassigned", label: "Not assigned" },
                  ],
                },
              ]
            : []
        }
        onSelectChange={(id: string, value: string) => {
          if (id === "semester") setSemesterFilter(value);
          if (id === "stream") setStreamFilter(value);
        }}
        showClear={Boolean(search || (tab === "courses" && (streamFilter !== "all" || semesterFilter !== "all")))}
        onClear={() => {
          setSearch("");
          if (tab === "courses") {
            setStreamFilter("all");
            setSemesterFilter("all");
          }
        }}
      />

      {tab === "courses" && unassignedCoursesCount > 0 && (
        <div className="bg-warning/10 border border-warning/30 rounded-xl p-3.5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-warning/20 text-warning flex items-center justify-center shrink-0">
              <AlertCircle size={16} />
            </div>
            <div>
              <p className="text-xs font-bold text-foreground">
                {unassignedCoursesCount} course{unassignedCoursesCount === 1 ? " is" : "s are"} unassigned to any semester
              </p>
              <p className="text-xs text-muted-foreground">
                Unassigned courses are hidden from the student study hub and semester modules until assigned to Semester 1 or 2.
              </p>
            </div>
          </div>
          {semesterFilter !== "unassigned" && (
            <button
              type="button"
              onClick={() => setSemesterFilter("unassigned")}
              className="px-3 py-1.5 bg-warning text-warning-foreground font-semibold rounded-lg text-xs hover:bg-warning/90 transition shadow-xs"
            >
              Filter Unassigned ({unassignedCoursesCount})
            </button>
          )}
        </div>
      )}

      {tab === "universities" && selectedUnis.size > 0 && (
        <div className="bg-primary/10 border border-primary/20 rounded-xl p-3 flex items-center justify-between flex-wrap gap-3">
          <span className="text-xs font-bold text-primary">
            {selectedUnis.size} university(ies) selected
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => runBulkUniAction("activate")}
              className="px-3 py-1.5 bg-success hover:bg-success text-success-foreground rounded-lg text-xs font-semibold flex items-center gap-1 transition disabled:opacity-50"
            >
              <Check size={13} /> Activate
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => runBulkUniAction("deactivate")}
              className="px-3 py-1.5 border border-border bg-card text-foreground hover:bg-muted rounded-lg text-xs font-semibold transition disabled:opacity-50"
            >
              Deactivate
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setBulkDeleteTarget("unis")}
              className="px-3 py-1.5 bg-destructive hover:bg-destructive text-destructive-foreground rounded-lg text-xs font-semibold flex items-center gap-1 transition disabled:opacity-50"
            >
              <Trash2 size={13} /> Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedUnis(new Set())}
              className="px-2.5 py-1.5 text-xs text-muted-foreground hover:text-foreground font-semibold"
            >
              Deselect
            </button>
          </div>
        </div>
      )}

      {tab === "courses" && selectedCourses.size > 0 && (
        <div className="bg-primary/10 border border-primary/20 rounded-xl p-3 flex items-center justify-between flex-wrap gap-3">
          <span className="text-xs font-bold text-primary">
            {selectedCourses.size} course(s) selected
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            {canWrite && (
              <>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setBulkMoveTarget({ targetSemester: 1, count: selectedCourses.size })}
                  className="px-3 py-1.5 bg-secondary hover:bg-muted text-foreground border border-border rounded-lg text-xs font-semibold flex items-center gap-1 transition disabled:opacity-50"
                >
                  Move to Semester 1
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setBulkMoveTarget({ targetSemester: 2, count: selectedCourses.size })}
                  className="px-3 py-1.5 bg-secondary hover:bg-muted text-foreground border border-border rounded-lg text-xs font-semibold flex items-center gap-1 transition disabled:opacity-50"
                >
                  Move to Semester 2
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setBulkMoveTarget({ targetSemester: null, count: selectedCourses.size })}
                  className="px-3 py-1.5 bg-secondary hover:bg-muted text-foreground border border-border rounded-lg text-xs font-semibold flex items-center gap-1 transition disabled:opacity-50"
                >
                  Unassign Semester
                </button>
              </>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={() => setBulkDeleteTarget("courses")}
              className="px-3 py-1.5 bg-destructive hover:bg-destructive text-destructive-foreground rounded-lg text-xs font-semibold flex items-center gap-1 transition disabled:opacity-50"
            >
              <Trash2 size={13} /> Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedCourses(new Set())}
              className="px-2.5 py-1.5 text-xs text-muted-foreground hover:text-foreground font-semibold"
            >
              Deselect
            </button>
          </div>
        </div>
      )}

      {tab === "universities" ? (
        <div className="bg-card rounded-lg border border-border overflow-hidden shadow-sm">
          <div className="overflow-x-auto admin-scroll-x">
            <table className="w-full text-sm">
              <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-4 py-3 w-10 text-center">
                  <button
                    type="button"
                    onClick={toggleAllUnis}
                    className="p-1 rounded hover:bg-muted text-muted-foreground transition"
                    title={allUnisSelected ? "Deselect all" : "Select all"}
                  >
                    {allUnisSelected ? (
                      <CheckSquare size={16} className="text-primary" />
                    ) : someUnisSelected ? (
                      <Minus size={16} className="text-primary" />
                    ) : (
                      <Square size={16} />
                    )}
                  </button>
                </th>
                {["Name", "Code", "Region", "Resources", "Students", "Status", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredUnis.length === 0 ? (
                <tr><td colSpan={8}><AdminEmptyState message={search ? "No universities matching your search" : "No universities yet"} /></td></tr>
              ) : filteredUnis.map((u) => (
                <tr key={u.id} className="hover:bg-muted/40">
                  <td className="px-4 py-3 w-10 text-center">
                    <button
                      type="button"
                      onClick={() => toggleUni(u.id)}
                      className="p-1 rounded hover:bg-muted text-muted-foreground transition"
                    >
                      {selectedUnis.has(u.id) ? (
                        <CheckSquare size={16} className="text-primary" />
                      ) : (
                        <Square size={16} />
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 font-semibold">{u.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.short_name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.region || "—"}</td>
                  <td className="px-4 py-3 font-semibold">{u.resourceCount}</td>
                  <td className="px-4 py-3">{u.studentCount}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${u.isActive ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}>
                      {u.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {canWrite && (
                      <div className="flex gap-1 justify-end">
                        <button type="button" onClick={() => openEditUni(u)} className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center" title="Edit">
                          <Pencil size={14} />
                        </button>
                        <button type="button" onClick={() => setDeleteTarget({ type: "uni", id: u.id, label: u.name })} className="w-8 h-8 rounded-lg hover:bg-destructive/10 text-destructive flex items-center justify-center" title="Delete / deactivate">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        </div>
      ) : tab === "courses" ? (
        <div className="bg-card rounded-lg border border-border overflow-hidden shadow-sm">
          <div className="overflow-x-auto admin-scroll-x">
            <table className="w-full text-sm">
              <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-4 py-3 w-10 text-center">
                  <button
                    type="button"
                    onClick={toggleAllCourses}
                    className="p-1 rounded hover:bg-muted text-muted-foreground transition"
                    title={allCoursesSelected ? "Deselect all" : "Select all"}
                  >
                    {allCoursesSelected ? (
                      <CheckSquare size={16} className="text-primary" />
                    ) : someCoursesSelected ? (
                      <Minus size={16} className="text-primary" />
                    ) : (
                      <Square size={16} />
                    )}
                  </button>
                </th>
                {["Cover", "Code", "Name", "Stream", "Semester", "Premium", "Resources", "Description", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredCourses.length === 0 ? (
                <tr>
                  <td colSpan={9}>
                    <AdminEmptyState
                      message={
                        search
                          ? "No courses matching your search"
                          : streamFilter !== "all"
                          ? "No courses found for the selected stream"
                          : "No courses yet"
                      }
                    />
                  </td>
                </tr>
              ) : filteredCourses.map((c) => (
                <tr key={c.id} className="hover:bg-muted/40">
                  <td className="px-4 py-3 w-10 text-center">
                    <button
                      type="button"
                      onClick={() => toggleCourse(c.id)}
                      className="p-1 rounded hover:bg-muted text-muted-foreground transition"
                    >
                      {selectedCourses.has(c.id) ? (
                        <CheckSquare size={16} className="text-primary" />
                      ) : (
                        <Square size={16} />
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    {c.imageUrl ? (
                      <img src={c.imageUrl} alt="" className="w-9 h-9 rounded-md object-cover border border-border/60" />
                    ) : (
                      <div className="w-9 h-9 rounded-md bg-muted/60 border border-border/40 flex items-center justify-center text-muted-foreground">
                        <ImageIcon size={16} />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs font-semibold">{c.code}</td>
                  <td className="px-4 py-3 font-semibold">{c.name}</td>
                  <td className="px-4 py-3">
                    {c.streamName ? (
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                        {c.streamName}
                      </span>
                    ) : (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border">
                        Not assigned
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {canWrite ? (
                      <select
                        value={c.semester === null ? "unassigned" : String(c.semester)}
                        onChange={(e) => {
                          const newSem = e.target.value === "unassigned" ? null : Number(e.target.value);
                          handleInlineSemesterChange(c, newSem);
                        }}
                        className={`text-xs font-semibold px-2 py-1 rounded-md border cursor-pointer transition focus:ring-1 focus:ring-primary ${
                          c.semester
                            ? "bg-secondary text-foreground border-border hover:border-primary/50"
                            : "bg-warning/10 text-warning border-warning/30 hover:border-warning"
                        }`}
                        title="Change course semester"
                      >
                        <option value="1">Semester 1</option>
                        <option value="2">Semester 2</option>
                        <option value="unassigned">Unassigned</option>
                      </select>
                    ) : (
                      <span
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                          c.semester
                            ? "bg-secondary text-foreground border-border"
                            : "bg-warning/10 text-warning border-warning/30"
                        }`}
                      >
                        {c.semester ? `Sem ${c.semester}` : "Unassigned"}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {canWrite ? (
                      <button
                        type="button"
                        onClick={() => handleToggleCatalogCoursePremium(c)}
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          (c.isPremium ?? c.is_premium) ? "bg-primary" : "bg-muted"
                        }`}
                        title={(c.isPremium ?? c.is_premium) ? "Course is Premium (click to turn off)" : "Course is Free (click to enable Premium)"}
                      >
                        <span
                          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
                            (c.isPremium ?? c.is_premium) ? "translate-x-4" : "translate-x-0"
                          }`}
                        />
                      </button>
                    ) : (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        (c.isPremium ?? c.is_premium) ? "bg-primary/10 text-primary border border-primary/20" : "bg-muted text-muted-foreground"
                      }`}>
                        {(c.isPremium ?? c.is_premium) ? "Premium" : "Free"}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-semibold">{c.resourceCount}</td>
                  <td className="px-4 py-3 text-muted-foreground max-w-[240px] truncate">{c.description || "—"}</td>
                  <td className="px-4 py-3">
                    {canWrite && (
                      <div className="flex gap-1 justify-end">
                        <button type="button" onClick={() => openEditCourse(c)} className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center">
                          <Pencil size={14} />
                        </button>
                        <button type="button" onClick={() => setDeleteTarget({ type: "course", id: c.id, label: c.name })} className="w-8 h-8 rounded-lg hover:bg-destructive/10 text-destructive flex items-center justify-center">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-card rounded-lg border border-border overflow-hidden shadow-sm">
          <div className="overflow-x-auto admin-scroll-x">
            <table className="w-full text-sm">
              <thead>
              <tr className="border-b border-border bg-muted/50">
                {["Stream", "Code", "Resources", "Students", "Status", "Description", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredStreams.length === 0 ? (
                <tr><td colSpan={7}><AdminEmptyState message={search ? "No streams matching your search" : "No streams yet"} /></td></tr>
              ) : filteredStreams.map((s) => (
                <tr key={s.id} className="hover:bg-muted/40">
                  <td className="px-4 py-3 font-semibold text-foreground">{s.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{s.code}</td>
                  <td className="px-4 py-3 font-semibold">{s.resourceCount}</td>
                  <td className="px-4 py-3">{s.studentCount}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.isActive ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}>
                      {s.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground max-w-[240px] truncate">{s.description || "—"}</td>
                  <td className="px-4 py-3">
                    {canWrite && (
                      <div className="flex gap-1 justify-end">
                        <button type="button" onClick={() => openEditStream(s)} className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center" title="Edit">
                          <Pencil size={14} />
                        </button>
                        <button type="button" onClick={() => setDeleteTarget({ type: "stream", id: s.id, label: s.name })} className="w-8 h-8 rounded-lg hover:bg-destructive/10 text-destructive flex items-center justify-center" title="Delete stream">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        </div>
      )}

      <ModalShell
        open={uniModalOpen}
        onClose={() => { setCreatingUni(false); setEditUni(null); }}
        title={editUni ? "Edit university" : uniCreateMode === "list" ? "Register universities by list" : "Add university"}
      >
        <form onSubmit={saveUniversity} className="space-y-3">
          {formError && <p className="text-sm text-destructive bg-destructive/10 p-2.5 rounded-lg border border-destructive/30">{formError}</p>}

          {!editUni && (
            <div className="flex rounded-lg bg-muted p-1 text-xs font-semibold mb-3">
              <button
                type="button"
                onClick={() => setUniCreateMode("single")}
                className={`flex-1 py-1.5 rounded-md transition ${uniCreateMode === "single" ? "bg-card shadow-xs text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                Single entry
              </button>
              <button
                type="button"
                onClick={() => setUniCreateMode("list")}
                className={`flex-1 py-1.5 rounded-md transition ${uniCreateMode === "list" ? "bg-card shadow-xs text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                Register by list
              </button>
            </div>
          )}

          {!editUni && uniCreateMode === "list" ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Enter one university per line in format: <strong>Name, ShortCode, Region</strong>
              </p>
              <textarea
                rows={6}
                value={uniBatchText}
                onChange={(e) => setUniBatchText(e.target.value)}
                placeholder={"Addis Ababa University, AAU, Addis Ababa\nJimma University, JU, Oromia\nBahir Dar University, BDU, Amhara\nHawassa University, HU, Sidama"}
                className="w-full px-3 py-2.5 rounded-lg border border-border text-xs font-mono bg-background"
              />
              <p className="text-xs text-muted-foreground">
                Detected: <span className="font-semibold text-primary">{parseUniversitiesList(uniBatchText).length}</span> universities ready to register
              </p>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-semibold mb-1">University Name <span className="text-destructive">*</span></label>
                <input value={uniForm.name} onChange={(e) => setUniForm({ ...uniForm, name: e.target.value })} placeholder="University name" required className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Short Code <span className="text-destructive">*</span></label>
                <input value={uniForm.short_name} onChange={(e) => setUniForm({ ...uniForm, short_name: e.target.value })} placeholder="Short code (e.g. AAU)" required className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm uppercase font-mono" />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Region (optional)</label>
                <input value={uniForm.region} onChange={(e) => setUniForm({ ...uniForm, region: e.target.value })} placeholder="Region (optional)" className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm" />
              </div>
              {editUni && (
                <button type="button" onClick={() => setUniForm({ ...uniForm, is_active: !uniForm.is_active })} className={`w-full py-2.5 rounded-lg border text-sm font-semibold transition ${uniForm.is_active ? "bg-success/10 text-success border-success/30" : "bg-destructive/10 text-destructive border-destructive/30"}`}>
                  Status: {uniForm.is_active ? "Active" : "Inactive"}
                </button>
              )}
            </>
          )}

          <button type="submit" disabled={busy} className="w-full py-2.5 bg-primary text-on-accent rounded-lg font-semibold text-sm disabled:opacity-60">
            {busy ? "Saving…" : !editUni && uniCreateMode === "list" ? `Register List (${parseUniversitiesList(uniBatchText).length})` : "Save"}
          </button>
        </form>
      </ModalShell>

      <ModalShell
        open={courseModalOpen}
        onClose={() => { setCreatingCourse(false); setEditCourse(null); }}
        title={editCourse ? "Edit course" : courseCreateMode === "list" ? "Register courses by list" : "Add course"}
      >
        <form onSubmit={saveCourse} className="space-y-3">
          {formError && <p className="text-sm text-destructive bg-destructive/10 p-2.5 rounded-lg border border-destructive/30">{formError}</p>}

          {!editCourse && (
            <div className="flex rounded-lg bg-muted p-1 text-xs font-semibold mb-3">
              <button
                type="button"
                onClick={() => setCourseCreateMode("single")}
                className={`flex-1 py-1.5 rounded-md transition ${courseCreateMode === "single" ? "bg-card shadow-xs text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                Single course
              </button>
              <button
                type="button"
                onClick={() => setCourseCreateMode("list")}
                className={`flex-1 py-1.5 rounded-md transition ${courseCreateMode === "list" ? "bg-card shadow-xs text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                Register by list
              </button>
            </div>
          )}

          {!editCourse && courseCreateMode === "list" ? (
            <div className="space-y-2.5">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-muted-foreground whitespace-nowrap block mb-1">Stream (optional):</label>
                  <select
                    value={courseBatchStreamId}
                    onChange={(e) => setCourseBatchStreamId(e.target.value)}
                    className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-border bg-background"
                  >
                    <option value="">Common / All streams</option>
                    {streams.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground whitespace-nowrap block mb-1">Semester:</label>
                  <select
                    value={courseBatchSemester === null ? "unassigned" : String(courseBatchSemester)}
                    onChange={(e) => setCourseBatchSemester(e.target.value === "unassigned" ? null : Number(e.target.value))}
                    className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-border bg-background font-semibold"
                  >
                    <option value="1">Semester 1</option>
                    <option value="2">Semester 2</option>
                    <option value="unassigned">Unassigned</option>
                  </select>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Enter one subject per line: <strong>Course Name, Code</strong>
              </p>
              <textarea
                rows={6}
                value={courseBatchText}
                onChange={(e) => setCourseBatchText(e.target.value)}
                placeholder={"General Chemistry, CHEM101\nGeneral Physics, PHYS101\nCommunicative English, ENGL101\nCivics & Ethical Education, CIV102"}
                className="w-full px-3 py-2.5 rounded-lg border border-border text-xs font-mono bg-background"
              />
              <p className="text-xs text-muted-foreground">
                Detected: <span className="font-semibold text-primary">{parseCoursesList(courseBatchText, courseBatchStreamId || undefined, courseBatchSemester).length}</span> courses ready to register
              </p>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-semibold mb-1">Course Name <span className="text-destructive">*</span></label>
                <input
                  value={courseForm.name}
                  onChange={(e) => setCourseForm({ ...courseForm, name: e.target.value })}
                  placeholder="e.g. General Chemistry"
                  required
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Course Code <span className="text-destructive">*</span></label>
                <input
                  value={courseForm.code}
                  onChange={(e) => setCourseForm({ ...courseForm, code: e.target.value })}
                  placeholder="e.g. CHEM101"
                  required
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm font-mono uppercase"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1">Target Stream (optional)</label>
                  <select
                    value={courseForm.stream_id}
                    onChange={(e) => setCourseForm({ ...courseForm, stream_id: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm"
                  >
                    <option value="">Common / All streams</option>
                    {streams.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1">Semester <span className="text-destructive">*</span></label>
                  <select
                    value={courseForm.semester === null ? "unassigned" : String(courseForm.semester)}
                    onChange={(e) => setCourseForm({ ...courseForm, semester: e.target.value === "unassigned" ? null : Number(e.target.value) })}
                    className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm font-semibold"
                  >
                    <option value="1">Semester 1</option>
                    <option value="2">Semester 2</option>
                    <option value="unassigned">Unassigned</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Cover Image (optional)</label>
                {courseForm.image_url ? (
                  <div className="relative w-full h-32 rounded-lg border border-border overflow-hidden group bg-muted/40 mb-2">
                    <img
                      src={courseForm.image_url}
                      alt="Course cover preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setCourseForm({ ...courseForm, image_url: "" })}
                      className="absolute top-2 right-2 p-1.5 bg-background/80 hover:bg-background text-foreground rounded-full shadow-sm transition"
                      title="Remove image"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <label className="border-2 border-dashed border-border hover:border-primary/50 transition rounded-lg p-3 flex flex-col items-center justify-center gap-1.5 cursor-pointer bg-muted/20 hover:bg-muted/40 text-center">
                    {uploadingCourseImage ? (
                      <Loader2 size={20} className="animate-spin text-primary" />
                    ) : (
                      <Upload size={20} className="text-muted-foreground" />
                    )}
                    <span className="text-xs font-medium text-foreground">
                      {uploadingCourseImage ? "Uploading & optimizing…" : "Click to upload course cover"}
                    </span>
                    <span className="text-xs text-muted-foreground">PNG, JPG, WebP up to 5MB (optimized to WebP)</span>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      disabled={uploadingCourseImage}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleCourseImageUpload(file);
                        e.target.value = "";
                      }}
                    />
                  </label>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1">Course Type (Pedagogy Profile)</label>
                  <select
                    value={courseForm.course_type || "LANGUAGE"}
                    onChange={(e) => setCourseForm({ ...courseForm, course_type: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm font-semibold"
                  >
                    {COURSE_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Controls question style (e.g. computational slips, language skills, or applied concepts).
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1">
                    Custom Extraction Profile <span className="text-muted-foreground font-normal">(optional)</span>
                  </label>
                  <textarea
                    value={courseForm.extraction_profile || ""}
                    onChange={(e) => setCourseForm({ ...courseForm, extraction_profile: e.target.value })}
                    placeholder="Overrides the default profile (e.g. specific grammar foci, reading skills)..."
                    rows={2}
                    className="w-full px-3 py-1.5 rounded-lg border border-border bg-background text-foreground text-xs"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Description (optional)</label>
                <textarea
                  value={courseForm.description}
                  onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                  placeholder="Optional course overview or details"
                  rows={3}
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm"
                />
              </div>
            </>
          )}

          <button type="submit" disabled={busy} className="w-full py-2.5 bg-primary text-on-accent rounded-lg font-semibold text-sm disabled:opacity-60">
            {busy ? "Saving…" : !editCourse && courseCreateMode === "list" ? `Register List (${parseCoursesList(courseBatchText).length})` : "Save"}
          </button>
        </form>
      </ModalShell>

      <ModalShell
        open={streamModalOpen}
        onClose={() => { setCreatingStream(false); setEditStream(null); }}
        title={editStream ? "Edit stream" : streamCreateMode === "list" ? "Register streams by list" : "Add stream"}
      >
        <form onSubmit={saveStream} className="space-y-3">
          {formError && <p className="text-sm text-destructive bg-destructive/10 p-2.5 rounded-lg border border-destructive/30">{formError}</p>}

          {!editStream && (
            <div className="flex rounded-lg bg-muted p-1 text-xs font-semibold mb-3">
              <button
                type="button"
                onClick={() => setStreamCreateMode("single")}
                className={`flex-1 py-1.5 rounded-md transition ${streamCreateMode === "single" ? "bg-card shadow-xs text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                Single stream
              </button>
              <button
                type="button"
                onClick={() => setStreamCreateMode("list")}
                className={`flex-1 py-1.5 rounded-md transition ${streamCreateMode === "list" ? "bg-card shadow-xs text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                Register by list
              </button>
            </div>
          )}

          {!editStream && streamCreateMode === "list" ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Enter stream names separated by lines or commas:
              </p>
              <textarea
                rows={5}
                value={streamBatchText}
                onChange={(e) => setStreamBatchText(e.target.value)}
                placeholder={"Natural Sciences\nSocial Sciences\nHealth Sciences\nEngineering and Technology"}
                className="w-full px-3 py-2.5 rounded-lg border border-border text-xs font-mono bg-background"
              />
              <p className="text-xs text-muted-foreground">
                Detected: <span className="font-semibold text-primary">{parseStreamsList(streamBatchText).length}</span> streams ready to register
              </p>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-semibold mb-1">Stream Name <span className="text-destructive">*</span></label>
                <input
                  value={streamForm.name}
                  onChange={(e) => setStreamForm({ ...streamForm, name: e.target.value })}
                  placeholder="e.g. Natural Sciences, Social Sciences, Health"
                  required
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Code (slug)</label>
                <input
                  value={streamForm.code}
                  onChange={(e) => setStreamForm({ ...streamForm, code: e.target.value })}
                  placeholder="e.g. natural, social"
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Description (optional)</label>
                <textarea
                  value={streamForm.description}
                  onChange={(e) => setStreamForm({ ...streamForm, description: e.target.value })}
                  placeholder="Optional description of this academic stream"
                  rows={3}
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm"
                />
              </div>
              {editStream && (
                <button
                  type="button"
                  onClick={() => setStreamForm({ ...streamForm, is_active: !streamForm.is_active })}
                  className={`w-full py-2.5 rounded-lg border text-sm font-semibold transition ${
                    streamForm.is_active ? "bg-success/10 text-success border-success/30" : "bg-destructive/10 text-destructive border-destructive/30"
                  }`}
                >
                  Status: {streamForm.is_active ? "Active" : "Inactive"}
                </button>
              )}
            </>
          )}

          <button type="submit" disabled={busy} className="w-full py-2.5 bg-primary text-on-accent rounded-lg font-semibold text-sm disabled:opacity-60">
            {busy ? "Saving…" : !editStream && streamCreateMode === "list" ? `Register List (${parseStreamsList(streamBatchText).length})` : "Save"}
          </button>
        </form>
      </ModalShell>

      <ConfirmDialog
        open={!!deleteTarget}
        title={
          deleteTarget?.type === "uni"
            ? "Remove university?"
            : deleteTarget?.type === "course"
            ? "Delete course?"
            : "Delete stream?"
        }
        message={
          deleteTarget?.type === "uni"
            ? `“${deleteTarget.label}” will be deactivated if it still has resources, otherwise permanently deleted.`
            : deleteTarget?.type === "course"
            ? `Delete “${deleteTarget?.label}”? This only works if no resources use it.`
            : `Delete stream “${deleteTarget?.label}”? Resources linked to this stream will no longer be restricted to it.`
        }
        confirmLabel="Delete"
        variant="destructive"
        loading={busy}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      <ConfirmDialog
        open={!!bulkDeleteTarget}
        title={bulkDeleteTarget === "unis" ? `Delete ${selectedUnis.size} universities?` : `Delete ${selectedCourses.size} courses?`}
        message={
          bulkDeleteTarget === "unis"
            ? `Are you sure you want to delete or deactivate the selected ${selectedUnis.size} universities? Universities linked to active resources will be deactivated instead.`
            : `Are you sure you want to delete the selected ${selectedCourses.size} courses? Courses linked to active resources cannot be deleted.`
        }
        confirmLabel={`Delete (${bulkDeleteTarget === "unis" ? selectedUnis.size : selectedCourses.size})`}
        variant="destructive"
        loading={busy}
        onClose={() => setBulkDeleteTarget(null)}
        onConfirm={confirmBulkDelete}
      />

      <ConfirmDialog
        open={!!bulkMoveTarget}
        title={
          bulkMoveTarget?.targetSemester
            ? `Move ${bulkMoveTarget?.count} courses to Semester ${bulkMoveTarget?.targetSemester}?`
            : `Unassign semester for ${bulkMoveTarget?.count} courses?`
        }
        message={
          bulkMoveTarget?.targetSemester
            ? `Are you sure you want to move the selected ${bulkMoveTarget?.count} course(s) to Semester ${bulkMoveTarget?.targetSemester}? Student access follows the new semester immediately. Course resources and active student entitlements are preserved.`
            : `Are you sure you want to unassign ${bulkMoveTarget?.count} course(s)? These courses will no longer be visible to students in the study hub or semester courses until reassigned.`
        }
        confirmLabel={bulkMoveTarget?.targetSemester ? `Move to Semester ${bulkMoveTarget?.targetSemester}` : "Unassign Semester"}
        variant="warning"
        loading={busy}
        onClose={() => setBulkMoveTarget(null)}
        onConfirm={confirmBulkMove}
      />

      <ConfirmDialog
        open={!!impactDialog}
        title="Impact: Course Semester Change"
        message={
          impactDialog
            ? `Changing “${impactDialog.courseName}” from ${
                impactDialog.currentSemester ? `Semester ${impactDialog.currentSemester}` : "Unassigned"
              } to ${
                impactDialog.newSemester ? `Semester ${impactDialog.newSemester}` : "Unassigned"
              } affects ${impactDialog.affectedStudentsCount} student(s) with active entitlements for Semester ${
                impactDialog.currentSemester
              }. They will immediately lose access to the course's ${
                impactDialog.resourceCount
              } learning resource(s) unless they also possess an active entitlement for Semester ${
                impactDialog.newSemester
              }. Course content and student subscriptions are preserved. Do you want to proceed?`
            : ""
        }
        confirmLabel="Confirm & Apply Change"
        variant="warning"
        loading={busy}
        onClose={() => setImpactDialog(null)}
        onConfirm={async () => {
          if (!impactDialog) return;
          setBusy(true);
          try {
            await impactDialog.onConfirm();
            setImpactDialog(null);
          } catch (err: any) {
            setError(err instanceof ApiError ? err.message : (err?.message || "Failed to change course semester"));
            setImpactDialog(null);
          } finally {
            setBusy(false);
          }
        }}
      />

      <ConfirmDialog
        open={Boolean(premiumConfirm)}
        title={
          premiumConfirm?.targetState
            ? `Set '${premiumConfirm.course.name}' as Premium?`
            : `Turn Off Premium on '${premiumConfirm?.course.name}'?`
        }
        message={
          premiumConfirm?.targetState
            ? `This course has ${premiumConfirm.resourceCount} resources. Setting this course to Premium will cause all its resources to effectively require a premium subscription. Individual resource labels are preserved and will be restored if turned off.`
            : `Turning off course premium will restore the individual Free / Premium labels for all ${premiumConfirm?.resourceCount} resources in this course.`
        }
        confirmLabel={premiumConfirm?.targetState ? "Make Course Premium" : "Turn Off Course Premium"}
        variant="primary"
        loading={busy}
        onClose={() => setPremiumConfirm(null)}
        onConfirm={confirmCatalogCoursePremium}
      />
    </div>
  );
}
